import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Phone, PhoneOff, Loader2, Save, User, Building2, Mail,
  MapPin, FileText, Sparkles, Brain, ArrowLeft, CalendarClock, Play, Pause,
  Calculator,
} from 'lucide-react';
import { api } from '../../utils/api';
import { useCallStore } from '../../stores/callStore';
import { useTelecallerHotkeys } from '../../hooks/useTelecallerHotkeys';
import { useTenantConfig } from '../../hooks/useTenantConfig';

interface LiveCall {
  id: string;
  status: string;
  phoneNumber: string;
  startTime: string | null;
  duration: number;
  transcription: string | null;
  transcriptionSummary?: string | null;
  sentiment: string | null;
  dispositionId?: string | null;
  notes?: string | null;
  recordingUrl?: string | null;
  leadId?: string | null;
  contactId?: string | null;
  contact?: {
    id: string;
    firstName: string;
    lastName: string;
    phone?: string;
    email?: string;
    account?: { id: string; name: string } | null;
  } | null;
}

interface Disposition {
  id: string;
  name: string;
  category: string;
  color: string;
}

interface LeadContext {
  lead?: any;
  contact?: {
    id: string;
    firstName: string;
    lastName: string;
    phone?: string;
    mobile?: string;
    email?: string;
    city?: string;
    productInterest?: string;
    account?: { id: string; name: string } | null;
  } | null;
  deal?: {
    id: string;
    title: string;
    value: number;
    stage?: { name: string; color: string };
  } | null;
  callHistory?: Array<{
    id: string;
    status: string;
    duration: number;
    startTime: string;
    disposition?: { name: string } | null;
  }>;
}

function fmtDur(sec: number): string {
  if (!sec) return '0:00';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function sentimentBadge(s: string | null | undefined): JSX.Element | null {
  if (!s) return null;
  const t = s.toLowerCase();
  const cls = t.includes('positive')
    ? 'bg-green-100 text-green-700 border-green-200'
    : t.includes('negative')
    ? 'bg-red-100 text-red-700 border-red-200'
    : 'bg-gray-100 text-gray-700 border-gray-200';
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${cls}`}>
      {s}
    </span>
  );
}

// ─── Audio Player (reused pattern from CallLogsPage) ─────────────────────

function AudioPlayer({ callId }: { callId: string }) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const audio = new Audio();
    audio.src = `/api/v1/telecaller/calls/${callId}/recording`;
    audio.preload = 'metadata';
    audioRef.current = audio;
    audio.addEventListener('loadedmetadata', () => {
      setDuration(audio.duration);
      setLoading(false);
    });
    audio.addEventListener('timeupdate', () => setCurrentTime(audio.currentTime));
    audio.addEventListener('ended', () => setPlaying(false));
    audio.addEventListener('error', () => { setError(true); setLoading(false); });
    return () => { audio.pause(); audio.src = ''; };
  }, [callId]);

  const toggle = () => {
    if (!audioRef.current) return;
    if (playing) audioRef.current.pause(); else audioRef.current.play();
    setPlaying(!playing);
  };

  if (error) return <p className="text-xs text-red-500">Recording unavailable</p>;
  if (loading) return (
    <div className="flex items-center gap-2 text-xs text-gray-400">
      <Loader2 size={14} className="animate-spin" /> Loading recording…
    </div>
  );

  return (
    <div className="flex items-center gap-3 rounded-lg bg-gray-50 px-3 py-2">
      <button onClick={toggle} className="flex h-8 w-8 items-center justify-center rounded-full bg-[var(--ink-0)] text-white hover:bg-[var(--ink-1)]">
        {playing ? <Pause size={14} /> : <Play size={14} className="ml-0.5" />}
      </button>
      <div className="flex-1">
        <input
          type="range"
          min={0}
          max={duration || 0}
          step={0.1}
          value={currentTime}
          onChange={(e) => {
            const t = parseFloat(e.target.value);
            if (audioRef.current) audioRef.current.currentTime = t;
            setCurrentTime(t);
          }}
          className="h-1.5 w-full cursor-pointer appearance-none rounded-full bg-gray-200 accent-[var(--ink-0)]"
        />
        <div className="mt-0.5 flex justify-between text-[10px] text-gray-400">
          <span>{fmtDur(Math.floor(currentTime))}</span>
          <span>{fmtDur(Math.floor(duration))}</span>
        </div>
      </div>
    </div>
  );
}

// ─── Page ───────────────────────────────────────────────────────────────

export function CallWorkspacePage(): JSX.Element {
  const { callId = '' } = useParams();
  const navigate = useNavigate();
  const hangup = useCallStore((s) => s.hangup);
  // Track D — Pool Calculator chip in-call. Visible IFF tenant has the
  // module enabled AND we have a lead bound to this call. Module-flag-driven
  // only; never tenant-slug-driven.
  const { enabledModules } = useTenantConfig();
  const poolCalcEnabled =
    enabledModules === null ||
    (Array.isArray(enabledModules) && enabledModules.includes('pool_heat_pump_calculator'));

  const [call, setCall] = useState<LiveCall | null>(null);
  const [callLoading, setCallLoading] = useState(true);
  const [leadContext, setLeadContext] = useState<LeadContext | null>(null);
  const [dispositions, setDispositions] = useState<Disposition[]>([]);
  const [endedBannerShown, setEndedBannerShown] = useState(false);

  // Form state
  const [dispositionId, setDispositionId] = useState<string>('');
  const [notes, setNotes] = useState('');
  const [followUpAt, setFollowUpAt] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const notesRef = useRef<HTMLTextAreaElement | null>(null);

  // ── Fetch dispositions once ──
  useEffect(() => {
    api.get('/telecaller/dispositions').then((res) => {
      setDispositions(res.data?.data ?? []);
    }).catch(() => setDispositions([]));
  }, []);

  // ── Live poll loop (5 s) ──
  useEffect(() => {
    if (!callId) return;
    let cancelled = false;
    let alreadyCompleted = false;

    const pull = async () => {
      try {
        // Prefer /live endpoint; fall back to main call GET if missing
        let data: any = null;
        try {
          const r = await api.get(`/telecaller/calls/${callId}/live`);
          data = r.data?.data ?? r.data;
        } catch {
          const r = await api.get(`/telecaller/calls/${callId}`);
          data = r.data?.data;
        }
        if (cancelled || !data) return;

        const c: LiveCall = {
          id: data.id ?? callId,
          status: data.status ?? 'UNKNOWN',
          phoneNumber: data.phoneNumber ?? '',
          startTime: data.startTime ?? null,
          duration: data.duration ?? 0,
          transcription: data.transcription ?? null,
          transcriptionSummary: data.transcriptionSummary ?? null,
          sentiment: data.sentiment ?? null,
          dispositionId: data.dispositionId ?? null,
          notes: data.notes ?? null,
          recordingUrl: data.recordingUrl ?? null,
          leadId: data.leadId ?? null,
          contactId: data.contactId ?? null,
          contact: data.contact ?? null,
        };
        setCall(c);
        setCallLoading(false);

        // Hydrate form state once when loaded
        if (c.dispositionId && !dispositionId) setDispositionId(c.dispositionId);
        if (c.notes && !notes) setNotes(c.notes);

        // Completed transition
        if (c.status === 'COMPLETED' && !alreadyCompleted) {
          alreadyCompleted = true;
          setEndedBannerShown(true);
          setTimeout(() => notesRef.current?.focus(), 50);
        }
      } catch {
        setCallLoading(false);
      }
    };

    pull();
    const id = setInterval(pull, 5000);
    return () => { cancelled = true; clearInterval(id); };
  }, [callId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Load lead context when we have a leadId/contactId ──
  useEffect(() => {
    const leadId = call?.leadId;
    if (!leadId) return;
    api.get(`/telecaller/leads/${leadId}/context`).then((res) => {
      setLeadContext(res.data?.data ?? null);
    }).catch(() => setLeadContext(null));
  }, [call?.leadId]);

  // ── Save disposition ──
  const handleSave = async () => {
    if (!callId) return;
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    try {
      await api.patch(`/telecaller/calls/${callId}`, {
        dispositionId: dispositionId || null,
        notes: notes || null,
      });
      if (followUpAt) {
        try {
          await api.post('/telecaller/followups', {
            contactId: call?.contactId,
            scheduledAt: new Date(followUpAt).toISOString(),
            notes: notes || null,
          });
        } catch {
          // Non-critical — disposition saved
        }
      }
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2500);
    } catch (err: any) {
      setSaveError(err?.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // ── Hotkeys ──
  useTelecallerHotkeys([
    {
      key: 'Enter',
      ctrl: true,
      allowInInputs: true,
      handler: (e) => {
        e.preventDefault();
        void handleSave();
      },
    },
    {
      key: 'Escape',
      handler: () => {
        void hangup();
      },
    },
  ]);

  const contactFromLead = leadContext?.contact;
  const contactFromCall = call?.contact;
  const displayContact = contactFromLead || contactFromCall;
  const displayName = displayContact
    ? `${displayContact.firstName} ${displayContact.lastName}`.trim()
    : call?.phoneNumber || 'Unknown';

  const statusColor = useMemo(() => {
    if (!call) return 'bg-gray-100 text-gray-600';
    const s = call.status;
    if (s === 'IN_PROGRESS' || s === 'RINGING') return 'bg-amber-100 text-amber-800';
    if (s === 'COMPLETED') return 'bg-emerald-100 text-emerald-800';
    if (s === 'FAILED') return 'bg-red-100 text-red-700';
    return 'bg-gray-100 text-gray-600';
  }, [call]);

  return (
    <div className="pb-20">
      {/* Header */}
      <div className="mb-4 flex items-center gap-3">
        <button
          onClick={() => navigate('/telecaller')}
          className="flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-sm text-gray-600 hover:bg-gray-50"
        >
          <ArrowLeft size={14} /> Back
        </button>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-[var(--text-primary)]">{displayName}</h1>
          <div className="flex items-center gap-2 text-xs text-[var(--text-muted)]">
            <span>{call?.phoneNumber}</span>
            {call && (
              <>
                <span>·</span>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${statusColor}`}>
                  {call.status.replace(/_/g, ' ')}
                </span>
                <span>·</span>
                <span className="tabular-nums">{fmtDur(call.duration)}</span>
              </>
            )}
          </div>
        </div>
        {poolCalcEnabled && call?.leadId && (
          <button
            onClick={() => navigate(`/sales/meeting/start?leadId=${call.leadId}`)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-[#0066b3] bg-[#f3f8ff] px-3 py-1.5 text-sm font-semibold text-[#0066b3] hover:bg-[#e1edfa]"
            title="Open the Sun Stellar pool heat pump calculator with this lead pre-bound"
          >
            <Calculator size={14} /> Pool Calculator
          </button>
        )}
        {call?.status !== 'COMPLETED' && (
          <button
            onClick={() => hangup()}
            className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-red-700"
          >
            <PhoneOff size={14} /> Hang up
          </button>
        )}
      </div>

      {/* Call-ended banner */}
      {endedBannerShown && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-2.5 text-sm text-emerald-800">
          <Phone size={14} /> Call ended — add your disposition below.
        </div>
      )}

      {/* Main 60/40 split */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-10">
        {/* Left: transcript */}
        <section className="lg:col-span-6 rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)]">
          <header className="flex items-center justify-between border-b border-[var(--border-primary)] px-4 py-3">
            <div className="flex items-center gap-2">
              <Brain size={14} className="text-indigo-600" />
              <h2 className="text-sm font-semibold text-[var(--text-primary)]">Live transcript</h2>
            </div>
            {sentimentBadge(call?.sentiment)}
          </header>
          <div className="min-h-[320px] p-4">
            {callLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 size={20} className="animate-spin text-[var(--saffron-600)]" />
              </div>
            ) : !call?.transcription ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="mb-2 rounded-full bg-indigo-50 p-3">
                  <Brain size={20} className="text-indigo-500" />
                </div>
                <p className="text-sm font-medium text-gray-700">
                  Waiting for transcript — call in progress
                </p>
                <p className="mt-1 text-xs text-gray-400">
                  Transcription appears after the call ends and audio is processed.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {call.transcriptionSummary && (
                  <div className="rounded-lg border border-indigo-200 bg-indigo-50/40 p-3 text-sm leading-relaxed text-gray-700">
                    <div className="mb-1 flex items-center gap-1 text-[11px] font-semibold uppercase text-indigo-700">
                      <Sparkles size={11} /> Summary
                    </div>
                    {call.transcriptionSummary}
                  </div>
                )}
                <div className="whitespace-pre-wrap rounded-lg border border-gray-200 bg-white p-3 text-sm text-gray-700">
                  {call.transcription}
                </div>
                {call.recordingUrl && (
                  <div>
                    <div className="mb-1.5 text-[11px] font-semibold uppercase text-gray-500">Recording</div>
                    <AudioPlayer callId={call.id} />
                  </div>
                )}
              </div>
            )}
          </div>
        </section>

        {/* Right: lead + disposition */}
        <section className="lg:col-span-4 space-y-4">
          {/* Lead context card */}
          <div className="rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)]">
            <header className="border-b border-[var(--border-primary)] px-4 py-3">
              <h2 className="text-sm font-semibold text-[var(--text-primary)]">Lead context</h2>
            </header>
            <div className="space-y-2 p-4 text-sm">
              {displayContact ? (
                <>
                  <div className="flex items-center gap-2">
                    <User size={13} className="text-gray-400" />
                    <span className="font-medium text-gray-900">
                      {displayContact.firstName} {displayContact.lastName}
                    </span>
                  </div>
                  {displayContact.account?.name && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <Building2 size={13} className="text-gray-400" />
                      <span>{displayContact.account.name}</span>
                    </div>
                  )}
                  {displayContact.phone && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <Phone size={13} className="text-gray-400" />
                      <span>{displayContact.phone}</span>
                    </div>
                  )}
                  {displayContact.email && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <Mail size={13} className="text-gray-400" />
                      <span className="truncate">{displayContact.email}</span>
                    </div>
                  )}
                  {(contactFromLead as any)?.city && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <MapPin size={13} className="text-gray-400" />
                      <span>{(contactFromLead as any).city}</span>
                    </div>
                  )}
                  {leadContext?.deal && (
                    <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-2">
                      <div className="text-[11px] font-semibold uppercase text-gray-500">Deal</div>
                      <div className="text-sm font-medium text-gray-800">{leadContext.deal.title}</div>
                      <div className="text-[11px] text-gray-500">
                        ₹{leadContext.deal.value?.toLocaleString('en-IN')}
                        {leadContext.deal.stage?.name ? ` · ${leadContext.deal.stage.name}` : ''}
                      </div>
                    </div>
                  )}
                  {leadContext?.callHistory && leadContext.callHistory.length > 0 && (
                    <div className="mt-3">
                      <div className="mb-1 text-[11px] font-semibold uppercase text-gray-500">Recent calls</div>
                      <ul className="space-y-1">
                        {leadContext.callHistory.slice(0, 3).map((h) => (
                          <li key={h.id} className="flex items-center justify-between text-[11px] text-gray-600">
                            <span>{new Date(h.startTime).toLocaleDateString('en-IN')}</span>
                            <span>{fmtDur(h.duration)}</span>
                            <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px]">{h.status}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              ) : (
                <p className="text-xs text-gray-400">
                  {callLoading ? 'Loading…' : 'No lead metadata available.'}
                </p>
              )}
            </div>
          </div>

          {/* Disposition form */}
          <div className="rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)]">
            <header className="border-b border-[var(--border-primary)] px-4 py-3">
              <h2 className="text-sm font-semibold text-[var(--text-primary)]">Disposition</h2>
            </header>
            <div className="space-y-3 p-4">
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">Outcome</label>
                <select
                  value={dispositionId}
                  onChange={(e) => setDispositionId(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
                >
                  <option value="">Select disposition…</option>
                  {dispositions.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}{d.category ? ` · ${d.category}` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">
                  <FileText size={11} className="mr-1 inline" />
                  Notes
                </label>
                <textarea
                  ref={notesRef}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={4}
                  placeholder="What happened on the call?"
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">
                  <CalendarClock size={11} className="mr-1 inline" />
                  Follow-up (optional)
                </label>
                <input
                  type="datetime-local"
                  value={followUpAt}
                  onChange={(e) => setFollowUpAt(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
                />
              </div>
              {saveError && (
                <p className="rounded-md bg-red-50 px-2 py-1 text-xs text-red-700">{saveError}</p>
              )}
              {saveSuccess && (
                <p className="rounded-md bg-emerald-50 px-2 py-1 text-xs text-emerald-700">Saved.</p>
              )}
              <button
                onClick={handleSave}
                disabled={saving}
                className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-semibold text-white hover:bg-[var(--ink-1)] disabled:opacity-50"
              >
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                Save disposition
                <span className="ml-1 hidden text-[10px] opacity-80 sm:inline">(Ctrl+Enter)</span>
              </button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
